export type RoomFact = { label: string, value: string }
export type RoomReview = { quote: string, name: string, avatar?: string }

export type RoomDetail = {
    slug: string
    name: string
    /** Chips with an empty label are not rendered. */
    bed: string
    guests: string
    size: string
    cardImage: string
    heroImage: string
    overviewImage: string
    galleryImage: string
    featuresImage: string
    imageAlts: {
        card: string
        hero: string
        overview: string
        gallery: string
        features: string
    }
    cardDescription: string
    /** Meta description for the cabin page, 140 to 160 characters. */
    seoDescription: string
    /** The first entry continues the sentence "The {name} ...". */
    overview: string[]
    amenities: string[]
    pricingTitle: string
    pricingSubtitle: string
    price: string
    priceIncludes: string
    goodToKnow: RoomFact[]
    /** The reviews block only renders when there are real reviews to show. */
    rating: string
    scores: { label: string, value: string }[]
    reviews: RoomReview[]
}

/** Resort policies, as published on the original reservations page. */
const stayPolicies: RoomFact[] = [
    { label: 'Check-in', value: 'Anytime after 3pm' },
    { label: 'Check-out', value: 'By 10am' },
    { label: 'Linens and towels', value: 'Provided' },
    { label: 'Pets', value: 'Welcome, with a crate' },
    { label: 'Deposit', value: '$200' }
]

const rates = {
    price: 'Call for rates',
    priceIncludes: 'Please contact us for weekly and nightly rates. In July and August we book week long stays, Sunday to Sunday.'
}

export const roomDetails: RoomDetail[] = [
    {
        slug: 'red-cabin',
        name: 'Red Cabin',
        bed: '2 Queen Beds',
        guests: 'Up to 6 Guests',
        size: '',
        cardImage: '/images/red-cabin-screened-porch.jpg',
        heroImage: '/images/red-cabin-kitchen-island.jpg',
        overviewImage: '/images/red-cabin-living-room.jpg',
        galleryImage: '/images/red-cabin-dining.jpg',
        featuresImage: '/images/red-cabin-porch-chairs.jpg',
        imageAlts: {
            card: "The Red Cabin's three season screened in porch under tall pines",
            hero: 'Kitchen island and living area under the pine ceiling of the Red Cabin',
            overview: 'Red Cabin living room with grey armchairs, a red rug and windows to the porch',
            gallery: 'Red Cabin dining table with seating for six beside the kitchen',
            features: 'Red chairs and a small table on the Red Cabin screened in porch'
        },
        cardDescription: 'Our four season cabin for up to 6 people, built over the winter of 2017, with a three season screened in porch.',
        seoDescription: "The Red Cabin at Bodin's Resort is a four season cabin on Lake Superior for up to 6 people, with two queen bedrooms and a three season screened in porch.",
        overview: [
            'is our Four Season accommodation for up to 6 people, built over the winter of 2017 by Scott Bodin Construction.',
            'Guests are just steps away from prime ice-fishing areas off of the shoreline, and hundreds of miles of snowmobile trails are a short ride away.',
            'Check out the ice road to Madeline Island, or the Ice Caves when they are open. Or just relax and see how quiet and calm the Winter Season can be.'
        ],
        amenities: [
            'Two bedrooms with queen size beds',
            'Full kitchen and dining room table',
            'Center island and bar counter seating for 3',
            'Queen sized sofa sleeper and 2 recliners',
            'Three season screened in porch',
            'Full bath with shower and tub',
            'Large screen DIRECTV',
            'Great views of the lake'
        ],
        pricingTitle: 'Four season comfort',
        pricingSubtitle: 'Available for long term winter rentals as well.',
        ...rates,
        goodToKnow: [
            ...stayPolicies,
            { label: 'Winter stays', value: 'Long term rentals' }
        ],
        rating: '',
        scores: [],
        reviews: []
    },
    {
        slug: 'yellow-cabin',
        name: 'Yellow Cabin',
        bed: '2 Queen Beds',
        guests: 'Up to 8 Guests',
        size: '',
        cardImage: '/images/yellow-cabin-porch-chairs.jpg',
        heroImage: '/images/sunrise-at-yellow-cabin.jpg',
        overviewImage: '/images/yellow-cabin-living-dining.jpg',
        galleryImage: '/images/yellow-cabin-exterior.jpg',
        featuresImage: '/images/yellow-cabin-stair-nook.jpg',
        imageAlts: {
            card: "Yellow Adirondack chairs on the Yellow Cabin's screened in porch",
            hero: 'Sunrise over Lake Superior beside the Yellow Cabin',
            overview: 'Yellow Cabin living area with a grey sofa and a long dining table',
            gallery: 'The two level Yellow Cabin among the pines',
            features: 'Cushioned sleeping nook under the stairs in the Yellow Cabin'
        },
        cardDescription: 'Two levels with a bedroom on each, two full bathrooms and a large three season porch, with room for up to 8 people.',
        seoDescription: "The Yellow Cabin at Bodin's Resort has two levels, two full bathrooms and a large screened in porch on Lake Superior, with room for up to 8 people.",
        overview: [
            'has two levels, with a bedroom on each level featuring new Queen size beds.',
            'A fully equipped kitchen makes eating in easy and affordable, and there is a cute sleeping area under the stairs for a child.',
            'A large three season screened in porch with a pull out sleeper makes room for up to 8 people.'
        ],
        amenities: [
            'A bedroom on each level',
            'New queen size beds',
            'Full size sofa sleeper',
            'Large three season screened in porch',
            'Pull out sleeper in the porch',
            '2 full bathrooms with stand-up showers',
            'Fully equipped kitchen with stove top and oven',
            'Sleeping area under the stairs for a child'
        ],
        pricingTitle: 'Room for the whole family',
        pricingSubtitle: 'A pull out sleeper in the porch makes room for up to 8 people.',
        ...rates,
        goodToKnow: stayPolicies,
        rating: '',
        scores: [],
        reviews: []
    },
    {
        slug: 'blue-and-gray-cabins',
        name: 'Blue & Gray Cabins',
        bed: '3 Bedrooms + Loft',
        guests: '',
        size: '',
        cardImage: '/images/blue-cabin-entrance.jpg',
        heroImage: '/images/gray-cabin-patio.jpg',
        overviewImage: '/images/blue-cabin-living-room.jpg',
        galleryImage: '/images/blue-cabin-lake-porch.jpg',
        featuresImage: '/images/blue-cabin-kitchen.jpg',
        imageAlts: {
            card: 'The front porch and blue door of the Blue Cabin',
            hero: 'The Gray Cabin and its patio with Lake Superior beyond the lawn',
            overview: 'Blue Cabin living room with large blue sofas',
            gallery: 'White wicker chairs on the Blue Cabin porch, looking out to the lake',
            features: 'The big kitchen and breakfast bar in the Blue Cabin'
        },
        cardDescription: 'Two cabins that can be rented together or separately, with a big kitchen and wet bar in Blue and a private lakeside patio at Gray.',
        seoDescription: "Rent the Blue and Gray Cabins at Bodin's Resort together or separately, with a big kitchen and wet bar, a loft, and a private patio facing Lake Superior.",
        overview: [
            'can be rented together or separately.',
            'Blue has a Master bedroom on the main level and a queen-sized bed in the loft, large sofas in the living room, a big kitchen and wet bar, and a large front porch.',
            'Gray has two bedrooms with a queen bed in each, a separate kitchen and living area, and a private patio on the north side looking at the lake and the gorgeous sunrises.'
        ],
        amenities: [
            'Blue: master bedroom on the main level',
            'Blue: queen-sized bed in the loft',
            'Blue: pull out sofa sleeper',
            'Blue: big kitchen and wet bar',
            'Blue: full bathroom with stand-up shower',
            'Blue: large front porch',
            'Gray: two bedrooms, a queen bed in each',
            'Gray: separate kitchen and living area',
            'Gray: bathroom and shower',
            'Gray: private patio facing the lake'
        ],
        pricingTitle: 'Together or separately',
        pricingSubtitle: 'The Blue and Gray can be rented together or separately, with extra seating for a big group.',
        ...rates,
        goodToKnow: stayPolicies,
        rating: '',
        scores: [],
        reviews: []
    }
]

export const getRoom = (slug: string): RoomDetail | undefined =>
    roomDetails.find((room) => room.slug === slug)
